import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn, userInitials } from '@/lib/utils';
import { Check, Loader2, Search, Store, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

export interface StoreItem {
  id: string;
  name: string;
  email?: string;
  logo_url?: string | null;
  is_primary: boolean;
  is_current?: boolean;
  is_blocked?: boolean;
  login_blocked?: boolean;
  read_only?: boolean;
}

interface StorePickerProps {
  stores: StoreItem[];
  currentStoreId?: string | null;
  onSelect: (store: StoreItem) => void;
  isLoading?: boolean;
  compact?: boolean;
}

const MotionDiv = motion.div;

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 8, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 300, damping: 24 } },
};

function StoreSkeleton({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex flex-col gap-2">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className={cn(
            'flex items-center gap-3 rounded-xl border border-colorBorder/50 bg-card/50 animate-pulse',
            compact ? 'p-2.5' : 'p-3.5',
          )}
        >
          <div className={cn('rounded-full bg-elevated/80', compact ? 'h-8 w-8' : 'h-10 w-10')} />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-28 rounded-md bg-elevated/80" />
            {!compact && <div className="h-3 w-40 rounded-md bg-elevated/60" />}
          </div>
          <div className="h-5 w-16 rounded-full bg-elevated/60" />
        </div>
      ))}
    </div>
  );
}

export function StorePicker({
  stores,
  currentStoreId,
  onSelect,
  isLoading = false,
  compact = false,
}: StorePickerProps) {
  const { t } = useTranslation('common');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectingId, setSelectingId] = useState<string | null>(null);

  const filteredStores = stores.filter((store) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      store.name.toLowerCase().includes(query) ||
      (store.email || '').toLowerCase().includes(query)
    );
  });

  const handleSelect = (store: StoreItem) => {
    setSelectingId(store.id);
    onSelect(store);
  };

  if (isLoading) {
    return <StoreSkeleton compact={compact} />;
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Search */}
      {stores.length >= 4 && (
        <div className="relative group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground transition-colors group-focus-within:text-primary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('search_store')}
            className={cn(
              'w-full h-10 pl-10 pr-10 rounded-xl border border-colorBorder/60 bg-card/80 backdrop-blur-sm',
              'text-sm text-colorText placeholder:text-muted-foreground/60',
              'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40',
              'transition-all duration-200',
            )}
          />
          <AnimatePresence>
            {searchQuery && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-elevated/80 text-muted-foreground transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Store list */}
      <MotionDiv
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="flex flex-col gap-2"
      >
        {filteredStores
          .filter((store) => !store.login_blocked)
          .map((store) => {
          const isCurrent = store.id === currentStoreId;
          const isSelecting = selectingId === store.id;

          return (
            <MotionDiv key={store.id} variants={itemVariants} layout>
              <button
                onClick={() => !isCurrent && !isSelecting && handleSelect(store)}
                disabled={isCurrent || isSelecting}
                className={cn(
                  'w-full flex items-center gap-3 rounded-xl border text-left transition-all duration-300 group/store',
                  compact ? 'p-2.5' : 'p-3.5',
                  isCurrent
                    ? 'border-primary/40 bg-primary/[0.04] shadow-[0_0_20px_rgba(169,211,66,0.06)]'
                    : 'border-colorBorder/50 bg-card/60 backdrop-blur-sm hover:bg-card hover:border-colorBorder hover:shadow-[0_2px_12px_rgba(0,0,0,0.08)] cursor-pointer',
                  isSelecting && 'opacity-70 pointer-events-none',
                )}
              >
                {/* Avatar with status ring */}
                <div className="relative flex-shrink-0">
                  <div
                    className={cn(
                      'rounded-full p-[2px] transition-all duration-300',
                      isCurrent
                        ? 'bg-gradient-to-br from-primary/60 to-primary/30'
                        : 'bg-transparent group-hover/store:bg-gradient-to-br group-hover/store:from-colorBorder group-hover/store:to-transparent',
                    )}
                  >
                    <Avatar className={cn('border-2 border-card', compact ? 'h-8 w-8' : 'h-10 w-10')}>
                      {store.logo_url && (
                        <AvatarImage src={store.logo_url} alt={store.name} className="object-cover" />
                      )}
                      <AvatarFallback
                        className={cn(
                          'text-xs font-semibold transition-colors duration-300',
                          isCurrent
                            ? 'bg-primary/15 text-primary'
                            : 'bg-elevated text-muted-foreground group-hover/store:bg-elevated/80',
                        )}
                      >
                        {userInitials(store.name)}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  {isCurrent && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center shadow-sm"
                    >
                      <Check className="w-2.5 h-2.5 text-primary-foreground" />
                    </motion.div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'font-medium truncate transition-colors',
                      compact ? 'text-xs' : 'text-sm',
                      isCurrent ? 'text-colorText' : 'text-colorText/90 group-hover/store:text-colorText',
                    )}
                  >
                    {store.name}
                  </p>
                  {!compact && store.email && (
                    <p className="text-xs text-muted-foreground/70 truncate mt-0.5">{store.email}</p>
                  )}
                </div>

                {/* Badge + Loading */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isSelecting ? (
                    <Loader2 className="w-4 h-4 text-primary animate-spin" />
                  ) : (
                    <>
                      {store.read_only && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-500/10 text-amber-400">
                          {t('read_only', { defaultValue: 'Somente leitura' })}
                        </span>
                      )}
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-all',
                          store.is_primary
                            ? 'bg-primary/10 text-primary'
                            : 'bg-[#60A5FA]/10 text-[#60A5FA]',
                        )}
                      >
                        {store.is_primary ? t('owner') : t('collaborator')}
                      </span>
                    </>
                  )}
                </div>
              </button>
            </MotionDiv>
          );
        })}

        {/* Empty state */}
        <AnimatePresence>
          {filteredStores.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex flex-col items-center justify-center py-10 text-center"
            >
              <div className="w-12 h-12 rounded-2xl bg-elevated/60 flex items-center justify-center mb-3">
                <Store className="w-6 h-6 text-muted-foreground/50" />
              </div>
              <p className="text-sm font-medium text-muted-foreground/80">{t('no_stores_found')}</p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-2 text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  {t('clear_search')}
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </MotionDiv>
    </div>
  );
}
